import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { api, relativeTime } from '@watchparty/shared';
import { tmdbImage, parsePayload } from '@/lib/utils';
import Avatar from '@/components/ui/Avatar';
// ---------------------------------------------------------------------------
// Event type constants (matching server ActivityEventType enum)
// ---------------------------------------------------------------------------
const EVENT_WATCHED = 1;
const EVENT_WATCHING = 2;
const EVENT_WANTS_TO_WATCH = 3;
const EVENT_RATED = 4;
const EVENT_WORK_ADDED = 5;
const EVENT_DISCUSSION_STARTED = 6;
const EVENT_WORK_SUGGESTED = 7;
// ---------------------------------------------------------------------------
// Format activity text
// ---------------------------------------------------------------------------
function formatActivityText(item) {
    const name = _jsx("strong", { children: item.displayName });
    const work = _jsx("strong", { children: item.workTitle });
    const payload = parsePayload(item.payload);
    switch (item.eventType) {
        case EVENT_WORK_ADDED:
            return _jsxs(_Fragment, { children: [name, " added ", work, " to the watch list"] });
        case EVENT_WATCHED:
            return _jsxs(_Fragment, { children: [name, " finished watching ", work] });
        case EVENT_WATCHING:
            return _jsxs(_Fragment, { children: [name, " started watching ", work] });
        case EVENT_WANTS_TO_WATCH:
            return _jsxs(_Fragment, { children: [name, " wants to watch ", work] });
        case EVENT_RATED: {
            const rating = payload.rating;
            if (rating != null) {
                return _jsxs(_Fragment, { children: [name, " rated ", work, " ", rating, " star", rating !== 1 ? 's' : ''] });
            }
            return _jsxs(_Fragment, { children: [name, " rated ", work] });
        }
        case EVENT_DISCUSSION_STARTED:
            return _jsxs(_Fragment, { children: [name, " started a discussion about ", work] });
        case EVENT_WORK_SUGGESTED:
            return _jsxs(_Fragment, { children: [name, " suggested adding ", _jsx(Link, { to: "/works/$workId", params: { workId: item.workId }, style: { color: 'inherit', fontWeight: 700 }, children: item.workTitle }), " to the watch list"] });
        default:
            return _jsxs(_Fragment, { children: [name, " did something"] });
    }
}
// ---------------------------------------------------------------------------
// Activity row
// ---------------------------------------------------------------------------
function ActivityRow({ item }) {
    return (_jsxs("div", { style: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '12px 0',
            borderBottom: '1px solid var(--color-border)',
        }, children: [_jsx(Avatar, { displayName: item.displayName, avatarUrl: item.avatarUrl, avatarColor: item.avatarColor, size: 32 }), _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("p", { style: {
                            margin: 0,
                            fontSize: '0.875rem',
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            lineHeight: 1.4,
                        }, children: formatActivityText(item) }), _jsx("p", { style: {
                            margin: '2px 0 0',
                            fontSize: '0.75rem',
                            color: 'var(--color-text-faint)',
                            fontFamily: 'var(--font-family-body)',
                        }, children: relativeTime(item.createdAt) })] }), item.posterUrl && (_jsx("div", { style: {
                    width: '28px',
                    height: '42px',
                    flexShrink: 0,
                    borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden',
                }, children: _jsx("img", { src: tmdbImage(item.posterUrl, 'w92'), alt: "", style: { width: '100%', height: '100%', objectFit: 'cover' } }) }))] }));
}
// ---------------------------------------------------------------------------
// ActivityFeedTab
// ---------------------------------------------------------------------------
export function ActivityFeedTab({ partyId }) {
    const { data, isLoading } = useQuery({
        queryKey: ['party', partyId, 'activity'],
        queryFn: () => api.get(`/parties/${partyId}/activity`),
        refetchInterval: 60_000,
    });
    const items = data?.items ?? [];
    if (isLoading) {
        return (_jsxs("div", { style: { display: 'flex', justifyContent: 'center', padding: '64px 0' }, children: [_jsx("style", { children: `@keyframes spin { to { transform: rotate(360deg) } }` }), _jsx("div", { style: {
                        width: '24px',
                        height: '24px',
                        border: '2px solid var(--color-border)',
                        borderTopColor: 'var(--color-text)',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite',
                    } })] }));
    }
    if (items.length === 0) {
        return (_jsxs("div", { style: { textAlign: 'center', padding: '64px 0' }, children: [_jsx(Activity, { size: 36, style: { color: 'var(--color-text-faint)', marginBottom: '12px', opacity: 0.4 } }), _jsx("p", { style: {
                        fontFamily: 'var(--font-family-body)',
                        fontSize: '0.875rem',
                        color: 'var(--color-text-faint)',
                    }, children: "No activity yet. Add something to the watch list to get started." })] }));
    }
    return (_jsx("div", { children: items.map((item) => (_jsx(ActivityRow, { item: item }, item.activityId))) }));
}
